import React, { useMemo, useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import DynamicInput from "components/dynamic/DynamicInput";
import { useTranslation } from "react-i18next";
import Button from "components/button/Button";
import { FaPlus, FaTrash } from "react-icons/fa";
import IconButton from "components/button/IconButton";
import { ScopeType } from "types/globalTypes";

interface Props {
  name: string;
  label: string;
  fields: any[];
  defaultItem: any;
  scope: ScopeType;
  isViewMode: boolean;
  type: "primary" | "secondary";
  required?: boolean;
  minItems?: number;
}

const ArrayItem = React.memo(
  ({
    fields,
    index,
    onRemove,
    scope,
    arrayName,
    isViewMode,
    canRemove = true,
  }: {
    fields: any[];
    index: number;
    onRemove: () => void;
    scope: ScopeType;
    arrayName: string;
    isViewMode: boolean;
    canRemove?: boolean;
  }) => {
    const { t } = useTranslation();

    const fieldsWithCorrectName = useMemo(() => {
      return fields.map((field) => ({
        ...field,
        name: `${arrayName}.${index}.${field.name}`,
      }));
    }, [fields, index, arrayName]);

    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-sm dark:border-white/10 dark:bg-gray-800/40">
        <div className="grid grid-cols-1">
          <DynamicInput fields={fieldsWithCorrectName} role={scope} />
        </div>

        {!isViewMode && canRemove && (
          <div className="mt-4 flex justify-end">
            <IconButton
              title={t("buttons.delete")}
              handler={onRemove}
              size="sm"
              icon={<FaTrash size={13} />}
              className="text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      </div>
    );
  }
);

ArrayItem.displayName = "ArrayItem";

export default function RHFArray({
  name,
  label,
  fields,
  defaultItem,
  scope,
  isViewMode,
  type,
  required = false,
  minItems = 0,
}: Props) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const {
    fields: items,
    append,
    remove,
  } = useFieldArray({
    control,
    name,
    keyName: "_id",
  });

  const isEmpty = items.length === 0;
  const isAppending = React.useRef(false);

  const handleAppend = useCallback(() => {
    if (isAppending.current) return;

    isAppending.current = true;

    const newItem = {
      ...defaultItem,
      _tempId: `${Date.now()}_${Math.random()}`,
    };

    append(newItem);

    setTimeout(() => {
      isAppending.current = false;
    }, 100);
  }, [append, defaultItem]);

  const handleRemove = useCallback(
    (index: number) => {
      if (index >= 0 && index < items.length && items.length > minItems) {
        remove(index);
      }
    },
    [items.length, minItems, remove]
  );

  const emptyState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-white/10 dark:bg-gray-800/40 sm:p-10">
        <div className="mb-2 text-4xl">📂</div>
        <h3 className="text-md font-bold text-gray-700 dark:text-gray-200">
          {t("company.array.noItemsTitle")}
        </h3>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {t("company.array.noItemsDescription")}
        </p>
        {required && minItems > 0 && (
          <p className="mt-2 text-xs text-red-500">
            {t("validation.minItemsRequired", { count: minItems })}
          </p>
        )}
      </div>
    ),
    [t, required, minItems]
  );

  return (
    <div className="space-y-4 rounded-3xl border border-gray-200 bg-white/70 p-4 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/60 sm:p-5">
      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3
            className={`text-base font-bold ${
              type === "primary"
                ? "text-brand-500 dark:text-brand-300"
                : "text-red-500 dark:text-red-300"
            } sm:text-lg`}
          >
            {label}
            {required && <span className="mr-1 text-sm text-red-500">*</span>}
          </h3>
        </div>

        {!isViewMode && (
          <Button
            type="button"
            size="sm"
            variant="primary"
            startIcon={<FaPlus size={13} />}
            onClick={handleAppend}
            className="w-full sm:w-auto"
          >
            {t("buttons.add")}
          </Button>
        )}
      </div>

      {isEmpty ? (
        emptyState
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, index) => (
            <ArrayItem
              key={item._id || `item-${index}`}
              fields={fields}
              index={index}
              onRemove={() => handleRemove(index)}
              scope={scope}
              arrayName={name}
              isViewMode={isViewMode}
              canRemove={items.length > minItems}
            />
          ))}
        </div>
      )}
    </div>
  );
}
